import { getContext, setContext } from 'svelte';
import { defaultFetchClient } from '@shared/clients/BrowserFetchClient.browser.ts';
import { DynamicEsiClient } from '@shared/components/dynamic-esi/DynamicEsiClient.ts';
import { log } from '@shared/log.browser.ts';
import type { EventsIncoming } from '@tilelist/application/ports/EventsIncoming.ts';
import type { TrackingPort } from '@tilelist/application/ports/TrackingPort.ts';
import type { VariationLoader } from '@tilelist/application/ports/VariationLoader.ts';
import { BenefitCinemaStore } from './benefit-cinema/BenefitCinemaStore.svelte.ts';
import { PandaComparison } from './product-comparison/PandaComparison.ts';

export interface BenefitCinemaState {
    readonly html: string | null;
    initFromServer(html: string): void;
    fetch(rule: string): Promise<void>;
}

export interface ComparisonCoordinator {
    initialize(context: Parameters<PandaComparison['initialize']>[0]): void;
    updateContext(context: Parameters<PandaComparison['updateContext']>[0]): Promise<void>;
    selectVariation: PandaComparison['selectVariation'];
    deselectVariation: PandaComparison['deselectVariation'];
    clearSelectedVariations: PandaComparison['clearSelectedVariations'];
    dispose(): void;
}

export interface ProviderComposition {
    createBenefitCinemaStore(): BenefitCinemaState;
    createComparisonCoordinator(
        variationLoader: VariationLoader,
        tracking: TrackingPort,
        eventsIncoming: EventsIncoming,
    ): ComparisonCoordinator;
}

export const PROVIDER_COMPOSITION_KEY: unique symbol = Symbol('ProviderComposition');

const DEFAULT_PROVIDER_COMPOSITION: ProviderComposition = {
    createBenefitCinemaStore: () =>
        new BenefitCinemaStore(
            new DynamicEsiClient({
                name: 'BenefitCinema',
                fetchClient: defaultFetchClient,
                logger: log,
            }),
        ),
    createComparisonCoordinator: (variationLoader, tracking, eventsIncoming) =>
        new PandaComparison(variationLoader, tracking, eventsIncoming),
};

export function provideProviderComposition(composition: ProviderComposition = DEFAULT_PROVIDER_COMPOSITION): void {
    setContext(PROVIDER_COMPOSITION_KEY, composition);
}

export function injectProviderComposition(): ProviderComposition {
    /*                                                                                                             */
    return getContext<ProviderComposition>(PROVIDER_COMPOSITION_KEY) ?? DEFAULT_PROVIDER_COMPOSITION;
}
